import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useApi } from "../hooks/use-api";
import { useAuth } from "../contexts/auth-context";
import { useWedding } from "../contexts/wedding-context";
import { queryKeys } from "../lib/query-keys";
import { staleTimes, queryClient } from "../lib/query-client";
import type { RsvpInput, RsvpStatus, Diet } from "@wedding/shared";
import { formatWeddingDate, listWeddingEvents, eventSourceFromRow } from "@wedding/shared";
import { useEventName } from "../lib/wedding-event";
import { cn } from "../lib/utils";

interface RsvpData {
  status: RsvpStatus;
  headcount: number;
  diet: Diet;
  notes: string;
}

import type { LucideIcon } from "lucide-react";
import {
  Loader2,
  Check,
  PartyPopper,
  HelpCircle,
  XCircle,
  Minus,
  Plus,
  ArrowRight,
} from "lucide-react";

const NOTES_MAX = 500;

const DIET_VALUES: Diet[] = ["none", "vegan", "vegetarian", "gluten_free", "other"];

/**
 * RSVP formundaki diyet etiketi.
 *
 * Önceki hali anahtar adını çalışma zamanında camelCase'e çevirip `as any` ile
 * susturuyordu (`rsvp.diet` + `gluten_free` → `dietGlutenFree`). İki sorun:
 * `pnpm check:i18n-keys` böyle bir çağrıyı hiç göremiyordu, ve `as any` yanlış
 * anahtar üretimini tipten de gizliyordu. Artık her dal literal → hem script
 * hem tsc denetliyor. `default` YOK: `Diet` yeni bir üye kazanırsa tsc kırar.
 *
 * ⚠️ Bu anahtarlar `guests.diet_*` ile AYNI DEĞİL — ayrı namespace, ayrı metin
 * (`rsvp.dietNone` = "Kısıtlama yok", `guests` tarafında `none` etiketi hiç yok).
 */
function dietRsvpLabel(t: (key: string) => string, diet: Diet): string {
  switch (diet) {
    case "none":        return t("rsvp.dietNone");
    case "vegan":       return t("rsvp.dietVegan");
    case "vegetarian":  return t("rsvp.dietVegetarian");
    case "gluten_free": return t("rsvp.dietGlutenFree");
    case "other":       return t("rsvp.dietOther");
  }
}

export function RsvpPage() {
  const { t, i18n } = useTranslation();
  const eventName = useEventName();
  const { guest } = useAuth();
  const api = useApi();
  const { slug, wedding } = useWedding();
  // Misafir hangi etkinliklere davetliyse hepsini görür (en yakın DEĞİL).
  const weddingEvents = listWeddingEvents(eventSourceFromRow(wedding));

  // Form state
  const [status, setStatus]       = useState<RsvpStatus>("yes");
  const [headcount, setHeadcount] = useState(1);
  const [diet, setDiet]           = useState<Diet>("none");
  const [notes, setNotes]         = useState("");

  // UI state
  const [hasExisting, setHasExisting] = useState(false);
  const [saved, setSaved]         = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const { data: rsvpResult, isLoading: loading } = useQuery({
    queryKey: queryKeys.wedding.rsvp(slug),
    queryFn: () => api.get<RsvpData | null>("/rsvp"),
    staleTime: staleTimes.rsvp,
    enabled: !!slug,
  });

  // Pre-fill form when data arrives from cache or fetch
  useEffect(() => {
    const d = rsvpResult?.data;
    if (!d) return;
    if (d.status) { setStatus(d.status); setHasExisting(true); }
    if (d.headcount) setHeadcount(d.headcount);
    if (d.diet) setDiet(d.diet);
    if (d.notes) setNotes(d.notes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsvpResult]);

  const { mutate: submitRsvp, isPending: saving } = useMutation({
    mutationFn: (input: RsvpInput) => api.post("/rsvp", input),
    onSuccess: () => {
      setHasExisting(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
      queryClient.invalidateQueries({ queryKey: queryKeys.wedding.rsvp(slug) });
    },
    onError: (err: any) => {
      setError(err?.message ?? t("common.error"));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    submitRsvp({ status, headcount, diet, notes } as RsvpInput);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const coverUrl = wedding?.bg_rsvp ?? null;

  const STATUS_OPTIONS: { value: RsvpStatus; label: string; icon: LucideIcon }[] = [
    { value: "yes",   label: t("rsvp.statusYes"),   icon: PartyPopper },
    { value: "maybe", label: t("rsvp.statusMaybe"), icon: HelpCircle  },
    { value: "no",    label: t("rsvp.statusNo"),    icon: XCircle     },
  ];

  return (
    /* Centre the card on desktop, stack on mobile */
    <div className="md:flex md:justify-center">
      <div className="w-full md:max-w-2xl bg-card rounded-2xl shadow-xl border border-primary/8 overflow-hidden">

        {/* ── Cover photo header ── */}
        <div className="relative h-44 md:h-64 w-full overflow-hidden">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/30 to-primary/5" />
          )}
          {/* Gradient overlay so text below is readable */}
          <div className="absolute inset-0 bg-gradient-to-t from-card via-black/20 to-transparent" />
        </div>

        {/* ── Form content ── */}
        <div className="px-6 md:px-10 pb-8 md:pb-10 -mt-10 relative z-10">

          {/* Title block */}
          <div className="text-center mb-7">
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
              {t("rsvp.shareStatus")}
            </h1>
            {/* Misafir davetli olduğu TÜM etkinlikleri görmeli — burada "en
                yakın" seçilmez, kronolojik sırayla hepsi listelenir. */}
            {weddingEvents.map((ev) => (
              <p key={ev.which} className="mt-1.5 text-sm text-primary font-medium">
                {[
                  weddingEvents.length > 1 ? eventName(ev) : null,
                  formatWeddingDate(ev.dateTime, i18n.language),
                  ev.location,
                ].filter(Boolean).join(" • ")}
              </p>
            ))}
            {weddingEvents.length === 0 && wedding?.location && (
              <p className="mt-1.5 text-sm text-primary font-medium">{wedding.location}</p>
            )}
          </div>

          {/* Success banner */}
          {saved && (
            <div className="flex items-center gap-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 mb-6">
              <Check className="h-5 w-5 text-emerald-500 shrink-0" />
              <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                {hasExisting ? t("rsvp.successUpdate") : t("rsvp.successNew")}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* ── Status options — full-width rows ── */}
            <div className="flex flex-col gap-2.5">
              {STATUS_OPTIONS.map(({ value, label, icon: Icon }) => {
                const active = status === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setStatus(value);
                      if (value === "no") { setHeadcount(1); setDiet("none"); }
                    }}
                    className={cn(
                      "flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-[background-color,border-color,color,box-shadow] cursor-pointer text-left",
                      active
                        ? "border-primary bg-primary text-white shadow-lg shadow-primary/20"
                        : "border-primary/15 bg-primary/3 hover:border-primary/40 text-foreground",
                    )}
                  >
                    <span className="text-base font-bold">{label}</span>
                    <Icon className={cn("h-5 w-5 shrink-0", active ? "text-white" : "text-muted-foreground")} />
                  </button>
                );
              })}
            </div>

            {/* ── Headcount ── */}
            <div className={cn("transition-[opacity,filter] duration-300", status === "no" && "opacity-30 blur-[2px] pointer-events-none select-none")}>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                  {t("rsvp.headcountLabel")}
                </p>
                <div className="flex items-center justify-between bg-primary/5 dark:bg-primary/8 rounded-xl border border-primary/10 px-5 py-4">
                  <span className="font-medium text-foreground">
                    {t("rsvp.headcountLabel")}
                  </span>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      aria-label={t("rsvp.decreaseHeadcount")}
                      onClick={() => setHeadcount(h => Math.max(1, h - 1))}
                      disabled={headcount <= 1}
                      className="size-8 flex items-center justify-center rounded-full bg-primary/20 text-primary hover:bg-primary hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <Minus className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <span aria-live="polite" className="text-xl font-bold w-6 text-center tabular-nums text-foreground">
                      {headcount}
                    </span>
                    <button
                      type="button"
                      aria-label={t("rsvp.increaseHeadcount")}
                      onClick={() => setHeadcount(h => Math.min(10, h + 1))}
                      disabled={headcount >= 10}
                      className="size-8 flex items-center justify-center rounded-full bg-primary/20 text-primary hover:bg-primary hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Diet pills ── */}
            <div className={cn("transition-[opacity,filter] duration-300", status === "no" && "opacity-30 blur-[2px] pointer-events-none select-none")}>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                  {t("rsvp.dietLabel")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {DIET_VALUES.map(d => {
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDiet(d)}
                        className={cn(
                          "rounded-full border px-4 py-1.5 text-sm font-medium transition-[background-color,border-color,color] cursor-pointer",
                          diet === d
                            ? "border-primary bg-primary text-white"
                            : "border-primary/20 text-muted-foreground hover:border-primary/50 hover:text-foreground",
                        )}
                      >
                        {dietRsvpLabel(t, d)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Notes ── */}
            <div className={cn("transition-[opacity,filter] duration-300", status === "no" && "opacity-30 blur-[2px] pointer-events-none select-none")}>
              <div className="flex items-end justify-between mb-2">
                <label htmlFor="rsvp-notes" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {t("rsvp.notesLabel")}
                </label>
                <span className={cn(
                  "text-xs tabular-nums",
                  notes.length > NOTES_MAX * 0.9 ? "text-rose-500" : "text-muted-foreground",
                )}>
                  {notes.length}/{NOTES_MAX}
                </span>
              </div>
              <textarea
                id="rsvp-notes"
                rows={3}
                maxLength={NOTES_MAX}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={t("rsvp.notesPlaceholder")}
                className="w-full resize-none rounded-xl border border-primary/10 bg-primary/3 dark:bg-primary/8 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary outline-none transition-[border-color,box-shadow] [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-rose-500 text-center">{error}</p>
            )}

            {/* ── Submit ── */}
            <button
              type="submit"
              disabled={saving}
              className={cn(
                "w-full flex items-center justify-center gap-2 rounded-xl py-4 font-bold text-sm shadow-lg transition-[background-color,opacity] cursor-pointer",
                saved
                  ? "bg-emerald-500 text-white shadow-emerald-500/20"
                  : "bg-primary text-white hover:opacity-90 shadow-primary/20",
                saving && "opacity-60 cursor-not-allowed",
              )}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                <Check className="h-4 w-4" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              {saving
                ? t("rsvp.submitting")
                : saved
                  ? t("rsvp.saved")
                  : hasExisting
                    ? t("rsvp.update")
                    : t("rsvp.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
